import { Box, Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { PageHeaderWrapper, Text, Title } from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings } from "@src/appV2/redesign/system/ui-theme";
import { useState } from "react";
import { useHistory } from "react-router-dom";

import { Button } from "../components/Button";
import { IconButton } from "../components/IconButton";
import { GoogleMapErrorBoundary } from "../GeoLocation/GoogleMapErrorBoundary";
import { SHIFT_DISCOVERY_LIST_PATH } from "../ShiftDiscovery/paths";
import { useGetEarlyAccessFacilities } from "./api/useGetEarlyAccessFacilities";
import { FacilityBottomSheet } from "./components/FacilityBottomSheet/FacilityBottomSheet";
import { EarlyAccessListContainer } from "./components/FacilityListView/EarlyAccessListContainer";
import { DEFAULT_MAP_ZOOM, FacilityMap } from "./components/FacilityMap/FacilityMap";
import { IntroModal } from "./components/IntroModal/IntroModal";
import { useEarlyAccessFacilitySelection } from "./hooks/useEarlyAccessFacilitySelection";

type ViewType = "map" | "list";

export function EarlyAccessPage() {
  const history = useHistory();
  const [currentView, setCurrentView] = useState<ViewType>("list");
  const [map, setMap] = useState<google.maps.Map | undefined>(undefined);
  const { data: facilities = [], isLoading } = useGetEarlyAccessFacilities();

  const {
    workerLocation,
    facilitiesWithDistance,
    selectedFacility,
    modalState,
    handleFacilitySelect,
  } = useEarlyAccessFacilitySelection({
    facilities,
  });

  const goBack = () => {
    // Always navigate to the main Find Shifts page, not through browser history
    history.replace(`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`);
  };

  return (
    <>
      <IntroModal />
      <Stack
        sx={(theme) => ({
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          backgroundColor: theme.palette.background.primary,
          height: "100vh",
          overflow: "hidden",
        })}
      >
        <PageHeaderWrapper
          sx={(theme) => ({
            backgroundColor: theme.palette.background.tertiary,
          })}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            position="relative"
            height={(theme) => theme.spacing(10)}
          >
            <Stack position="absolute" left={(theme) => theme.spacing(3)}>
              <IconButton
                title="Go Back"
                iconType="chevron-left"
                variant="outlined"
                onClick={goBack}
              />
            </Stack>
          </Stack>
          <Stack px={6} pt={2} pb={4} spacing={3} alignItems="center">
            <Stack alignItems="center" spacing={0.5}>
              <Title semibold component="h1" variant="h3" sx={{ textAlign: "center" }}>
                Mark Interest for
                <Box component="br" />
                Future Facilities
              </Title>
            </Stack>
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
              <Button
                variant={currentView === "list" ? "contained" : "outlined"}
                size="small"
                sx={(theme) => ({
                  paddingX: theme.spacing(4),
                  paddingY: theme.spacing(2),
                })}
                onClick={() => {
                  setCurrentView("list");
                  logEvent(APP_V2_USER_EVENTS.EARLY_ACCESS_FACILITIES_LIST_VIEW_CLICKED);
                }}
              >
                List View
              </Button>
              <Button
                variant={currentView === "map" ? "contained" : "outlined"}
                size="small"
                sx={(theme) => ({
                  paddingX: theme.spacing(4),
                  paddingY: theme.spacing(2),
                  minWidth: "120px",
                })}
                onClick={() => {
                  setCurrentView("map");
                  if (map && workerLocation) {
                    map.setCenter(workerLocation);
                    map.setZoom(DEFAULT_MAP_ZOOM);
                  }
                  logEvent(APP_V2_USER_EVENTS.EARLY_ACCESS_FACILITIES_NEAR_ME_CLICKED);
                }}
              >
                📍 Near Me
              </Button>
            </Stack>
          </Stack>
        </PageHeaderWrapper>

        <Stack
          sx={(theme) => ({
            flex: 1,
            position: "relative",
            width: "100%",
            overflow: "hidden",
            backgroundColor: theme.palette.background.tertiary,
            padding: getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(4) }),
          })}
        >
          {currentView === "map" ? (
            <GoogleMapErrorBoundary>
              <FacilityMap
                facilities={facilitiesWithDistance}
                workerLocation={workerLocation}
                mapState={[map, setMap]}
                onFacilitySelect={handleFacilitySelect}
              />
            </GoogleMapErrorBoundary>
          ) : (
            <EarlyAccessListContainer
              facilities={facilitiesWithDistance}
              workerLocation={workerLocation}
              isLoading={isLoading}
              onFacilitySelect={handleFacilitySelect}
            />
          )}
        </Stack>

        <Stack
          sx={(theme) => ({
            padding: 6,
            paddingBottom: 8,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.tertiary,
          })}
        >
          <Text
            variant="body2"
            color={(theme) => theme.palette.text.secondary}
            sx={{ textAlign: "center" }}
          >
            These facilities are not yet on Clipboard. Express your interest to get first dibs when
            they join!
          </Text>
        </Stack>

        {selectedFacility && (
          <FacilityBottomSheet facility={selectedFacility} modalState={modalState} />
        )}
      </Stack>
    </>
  );
}
